import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import i18n from './i18n'; // Initialize i18n before App
import App, { persistor } from './App';
import reportWebVitals from './reportWebVitals';
import { initAnalytics, onConsentUpdated } from './analytics';
import { initSentry, addBreadcrumb } from './utils/sentry';
import { onConsentUpdated as subscribeConsent } from './utils/cookieConsent';
import { initSessionWatch } from './utils/sessionWatch';
import { initNativeShell, hideNativeSplash } from './native/shell';
import { initOTA } from './native/ota';

// Shell natif (iOS/Android) : no-op sur le web. Initialisé tôt pour la barre
// de statut, le splash, le réseau et le bouton retour.
initNativeShell();
// OTA (Capgo) : signale que le bundle courant fonctionne (anti-rollback).
initOTA();
// Note : le push natif (permission + token) est initialisé au moment du login
// (via requestForToken) pour ne pas demander la permission dès l'ouverture.

// Veille de session : si le JWT est expiré, on déconnecte proprement et on
// redirige vers /login (même après plusieurs heures d'inactivité).
initSessionWatch();

// Sentry est initialisé APRÈS le premier rendu (initSentry), hors bundle initial.

initAnalytics();
subscribeConsent(onConsentUpdated);

// Navigation guard — intercepte window.history.pushState/replaceState AVANT React Router
// pour pouvoir bloquer la navigation depuis l'écran de création de bien.
// window.__navigationGuard = { blocked: true/false, pending: null, onBlock: () => {} }
const origPushState = window.history.pushState.bind(window.history);
const origReplaceState = window.history.replaceState.bind(window.history);
window.history.pushState = function(...args) {
  const g = window.__navigationGuard;
  if (g?.blocked) {
    const url = args[2] ? new URL(args[2], window.location.origin) : null;
    if (!url || url.pathname === window.location.pathname) return origPushState(...args);
    const path = url?.pathname || "";
    const isAllowed = path.startsWith("/property/add") || path.startsWith("/property/edit") || path.startsWith("/login");
    if (isAllowed) return origPushState(...args);
    g.pending = { type: "push", args };
    g.onBlock?.(path);
    return;
  }
  return origPushState(...args);
};
window.history.replaceState = function(...args) {
  const g = window.__navigationGuard;
  if (g?.blocked) {
    const url = args[2] ? new URL(args[2], window.location.origin) : null;
    if (!url || url.pathname === window.location.pathname) return origReplaceState(...args);
    const path = url?.pathname || "";
    const isAllowed = path.startsWith("/property/add") || path.startsWith("/property/edit") || path.startsWith("/login");
    if (isAllowed) return origReplaceState(...args);
    g.pending = { type: "replace", args };
    g.onBlock?.(path);
    return;
  }
  return origReplaceState(...args);
};


const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// On GARDE le HTML pré-rendu (déjà traduit) affiché tant que i18n ET le store
// persistant ne sont pas prêts, puis on rend en UNE seule fois. Sans cela, le
// premier render affichait les clés i18n non traduites (« cookieConsent… »), le
// « loading ... » du PersistGate et la bannière cookies → flashs au refresh.
const i18nAny = i18n as unknown as {
  isInitialized: boolean;
  on: (event: string, cb: () => void) => void;
};

function whenI18nReady(): Promise<void> {
  if (i18nAny.isInitialized) return Promise.resolve();
  return new Promise((resolve) => {
    i18nAny.on('initialized', () => resolve());
    i18nAny.on('loaded', () => { if (i18nAny.isInitialized) resolve(); });
  });
}

function whenStoreReady(): Promise<void> {
  if (!persistor || persistor.getState().bootstrapped) return Promise.resolve();
  return new Promise((resolve) => {
    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        unsubscribe();
        resolve();
      }
    });
  });
}

function renderApp() {
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );
}

// Garde-fou : on rend au plus tard après 6 s (réseau lent / i18n indisponible).
Promise.race([
  Promise.all([whenI18nReady(), whenStoreReady()]),
  new Promise<void>((resolve) => setTimeout(resolve, 6000)),
]).then(() => {
  renderApp();
  // Masque le splash APRÈS le premier rendu (évite le flash noir).
  requestAnimationFrame(() => setTimeout(() => hideNativeSplash(), 60));
  // Sentry chargé après le premier rendu (sort du bundle initial).
  initSentry();
});

// UI mount class is now set by App on mount to avoid races

// Mesures de performance (Web Vitals) : si tu veux les voir dans Sentry,
// passe une fonction de rappel ici. Par défaut on les consomme (compatible
// Sentry Performance) sans bloquer le rendu.
reportWebVitals(({ name, delta, id, value }) => {
  try {
    if (name === "CLS") addBreadcrumb({ category: "web-vitals", message: `${name}: ${value ?? delta}` });
  } catch (e) { /* ignore */ }
});
